/*
 * The app's state and actions over the core API. The UI never talks SIP:
 * it sends core commands and renders from core events, like the desktop
 * app's CoreBridge.
 */
import { computed, effect, signal } from "@preact/signals";
import { Core, CORE_VERSION } from "../core/core";
import { MediaModule } from "../core/media";
import { SipJsStack } from "../core/sipjs";
import type { CallInfo, CoreEvent, Json } from "../core/types";
import type { BookContact, ChatPartner, DeviceInfo, SettingEntry } from "../prov/client";
import { CallLogModule, type CallDevice, type CallEntry } from "../prov/calllog";
import { ChatModule } from "../prov/chat";
import { ContactsModule, normalizeNumber } from "../prov/contacts";
import { VoicemailModule, voicemailTarget } from "../prov/voicemail";
import { ProvModule, ProvState } from "../prov/managed";
import { KV, openKV } from "../prov/store";
import { numberOf } from "../embed/bridge";
import type { SiteConfig } from "./config";
import { Lang, LangMode, setDocumentLang, systemLang, t } from "./i18n";
import { applyTheme, ThemeMode } from "./theme";

export type Page = "dialer" | "contacts" | "messages" | "history" | "voicemail" | "settings" | "diagnostics";

export interface UiCall extends CallInfo {
  stats?: Json;
  ended_reason?: string;
}

export interface HistoryEntry {
  id: string;
  peer: string;
  number: string;
  name?: string;
  dir: "in" | "out";
  missed: boolean;
  at: number;
  duration: number;
  /** the line it was on */
  account?: string;
}

/** A line of this device, as Settings and the dialer name it */
export interface Line {
  id: string;
  /** the extension (the address's user) */
  ext: string;
  /** the provisioned name ("Réception"), "" none */
  name: string;
  /** position in the provisioning, 1 first */
  position: number;
  /** the outside number its calls show, "" unknown */
  callerId: string;
}

export interface Contact {
  id: string;
  name: string;
  /** the primary number (else the first) */
  number: string;
  /** every number of the contact, normalized (caller names) */
  numbers?: string[];
  company?: string;
  /** "local": the user's own book (synced), "shared": a company book (read only), "directory" */
  source?: "local" | "shared" | "directory";
  favorite?: boolean;
  bookName?: string;
  /** the core's contact, kept whole so an edit doesn't drop what the page doesn't show */
  raw?: Json;
}

export interface Prefs {
  theme: ThemeMode;
  lang: LangMode;
  dnd: boolean;
  source?: string;
  player?: string;
  alert?: string;
  notifications?: boolean;
}

interface ManualAccount {
  user: string;
  domain: string;
  auth_user?: string;
  password?: string;
  /** instead of the password (lab lines, tests) */
  ha1?: string;
  display_name?: string;
}

// --- state --------------------------------------------------------------
export const ready = signal(false);
export const mode = signal<"" | "managed" | "manual">("");
export const provState = signal<ProvState>("off");
export const pairing = signal<{ code: string; expiresAt: number } | null>(null);
export const provNotice = signal("");
export const accounts = signal<Record<string, { state: string; reason?: string; label: string }>>({});
/** what account.list says of each line: names, extension, order */
export const lineInfo = signal<Record<string, Line>>({});
/** the line chosen for the next call ("" the default one) */
export const outgoing = signal("");
export const calls = signal<UiCall[]>([]);
export const conference = signal<string[]>([]);
export const settings = signal<Record<string, SettingEntry>>({});
export const directory = signal<{ name?: string; entries: { name: string; number: string }[] }>({ entries: [] });
export const history = signal<HistoryEntry[]>([]);
/** the shared call list (prov/calllog.ts): on when the company turned it on */
export const callLog = signal<{ enabled: boolean; device: string; older: boolean; entries: CallEntry[] }>({
  enabled: false,
  device: "",
  older: false,
  entries: [],
});
export const contacts = signal<Contact[]>([]);
export const prefs = signal<Prefs>({ theme: "system", lang: "system", dnd: false });
export const page = signal<Page>("dialer");
export const missed = signal(0);
export const toast = signal<{ text: string; n: number } | null>(null);
export const dtmfCall = signal(""); // call whose keypad is open
export const site = signal<SiteConfig | null>(null);
export const micLevel = signal<{ rms_db?: number; peak_db?: number; error?: string } | null>(null);
export const audioBlocked = signal(false);
/** framed by another page (a dispatch or CRM app, src/embed) */
export const embedded = typeof window !== "undefined" && window.parent !== window;

// voicemail (device voicemail API): available once it answered
export interface VmMessage {
  id: string;
  line: number;
  from: { number: string | null; name: string | null };
  received_at: string | null;
  duration_ms: number | null;
  read: boolean;
  saved: boolean;
}
export interface VmGreetings {
  active_slot: number | null;
  slots: { slot: number; recorded_at: string | null; duration_ms: number | null }[];
  name: { recorded_at: string | null } | null;
  max_seconds: number;
}
// chat between users (prov/chat.ts)
export interface ChatConv {
  id: string;
  as: string; // which of my extensions
  kind: string;
  peer?: string;
  title?: string | null;
  members: { address: string; read_seq: number }[];
  last_at: number;
  unread: number;
  last?: ChatMsg;
}
export interface ChatFile {
  id?: string;
  name: string;
  type: string;
  size: number;
  url?: string;
  blob?: Blob;
}
export interface ChatMsg {
  id: string;
  attachments?: ChatFile[];
  seq: number;
  from: string;
  body: string;
  created_at: number;
  edited_at?: number | null;
  deleted: boolean;
  pending: boolean;
  mine: boolean;
}
export const chatEnabled = signal(false);
export const chatFiles = signal(false); // files can go (the server's files service)
export const chatUnread = signal(0);
export const chatMe = signal("");
export const chatMine = signal<string[]>([]); // every extension of this device, line 1 first
export const chatPartners = signal<ChatPartner[]>([]); // other companies' extensions open to us
export const chatFilter = signal(""); // the extension shown ("": all)
export const chats = signal<ChatConv[]>([]);
export const chatOpen = signal(""); // the open conversation ("new:<address>" before the first message)
export const chatMessages = signal<ChatMsg[]>([]);

export const vmAvailable = signal(false);
export const vmMessages = signal<VmMessage[]>([]);
export const vmGreetings = signal<VmGreetings | null>(null);
export const vmError = signal("");

export const registration = computed(() => {
  const s = Object.values(accounts.value);
  if (!s.length) return "none";
  if (s.some((a) => a.state === "registered")) return "registered";
  if (s.some((a) => a.state === "failed")) return "failed";
  return "registering";
});
/** the device's lines in provisioning order */
export const lines = computed(() =>
  Object.keys(accounts.value)
    .map((id) => lineInfo.value[id] ?? { id, ext: accounts.value[id].label, name: "", position: 99, callerId: "" })
    .sort((a, b) => a.position - b.position || a.id.localeCompare(b.id)),
);
/** calls.default_account (a setting of the user), else line 1 */
export const defaultAccount = computed(() => {
  const want = String(settings.value["calls.default_account"]?.v ?? "");
  const ids = lines.value.map((l) => l.id);
  return ids.includes(want) ? want : (ids[0] ?? "");
});
/** kept for callers that want "the" line (diagnostics, the embedding host) */
export const firstAccount = defaultAccount;

/** a North American number as people write it: (514) 907-0060 */
export function prettyNumber(n: string): string {
  const d = n.replace(/\D/g, "").replace(/^1(?=\d{10}$)/, "");
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : n;
}

/** "Réception (610)", or "610" */
export function lineTitle(id: string): string {
  const l = lines.value.find((x) => x.id === id);
  if (!l) return accounts.value[id]?.label ?? id;
  return l.name && l.name !== l.ext ? `${l.name} (${l.ext})` : l.ext;
}

/** the line of the shared call list's "company/610" */
export function lineOfExt(as: string | undefined): string | undefined {
  const ext = as?.split("/").pop();
  return ext ? lines.value.find((l) => l.ext === ext)?.id : undefined;
}

async function refreshLines(): Promise<void> {
  const r = await cmd({ cmd: "account.list" });
  if (!r.ok) return;
  const out: Record<string, Line> = {};
  for (const a of ((r.data as Json)?.accounts as Json[]) ?? []) {
    const id = String(a.id);
    out[id] = {
      id,
      ext: numberOf(String(a.aor ?? "")),
      name: String(a.display_name ?? ""),
      position: Number(a.line) || 99,
      callerId: String(a.caller_id ?? ""),
    };
  }
  lineInfo.value = out;
  if (outgoing.value && !out[outgoing.value]) outgoing.value = "";
}
export const setupNeeded = computed(
  () => ready.value && (mode.value === "" || (mode.value === "managed" && provState.value !== "active")),
);
export const liveCalls = computed(() => calls.value.filter((c) => c.state !== "ended"));
export const managedReady = computed(() => mode.value === "managed" && provState.value === "active");

let core: Core;
let kv: KV;
let stack: SipJsStack;
const answered = new Set<string>(); // incoming calls answered or declined here
const history0 = new Map<string, { established?: number }>();

export function cmd(c: Json): Promise<CoreEvent> {
  return core.command(c as never);
}

/** every core event, after the app's own handling (the embedding bridge) */
export const coreListeners = new Set<(e: CoreEvent) => void>();

export function notice(text: string): void {
  toast.value = { text, n: (toast.value?.n ?? 0) + 1 };
}

// --- start --------------------------------------------------------------
export async function boot(cfg: SiteConfig): Promise<void> {
  site.value = cfg;
  kv = await openKV();
  prefs.value = { ...prefs.value, ...((await kv.get<Prefs>("prefs")) ?? {}) };
  applyPrefs();
  history.value = (await kv.get<HistoryEntry[]>("history")) ?? [];

  stack = new SipJsStack(cfg.wss_url, cfg.ice_servers, `MocTel Vox Web/${CORE_VERSION}`);
  core = new Core(stack, { wss_url: cfg.wss_url, stats_interval: 2 }, onEvent);
  core.use(new MediaModule(core, stack, "/ringtone.mp3"));
  const prov = new ProvModule(core, kv);
  const book = new ContactsModule(core, kv);
  prov.attachContacts(book);
  const chat = new ChatModule(core, kv);
  prov.attachChat(chat);
  const callLogModule = new CallLogModule(core, kv);
  prov.attachCallLog(callLogModule);
  core.use(prov);
  core.use(book);
  core.use(chat);
  core.use(callLogModule);
  core.use(new VoicemailModule(prov));
  core.start();
  await migrateLocalContacts();
  const listed = await cmd({ cmd: "contacts.list" });
  if (listed.ok) setContacts(listed.data as Json);
  await refreshChat();
  // the messages page on screen: what arrives is read
  effect(() => {
    if (page.value === "messages" && chatOpen.value) void markChatRead();
  });
  addEventListener("focus", () => void markChatRead());
  await cmd({ cmd: "core.dnd", enable: prefs.value.dnd });
  await restoreDevices();

  // activated: does this server have the voicemail API?
  effect(() => {
    if (provState.value === "active") void vmRefresh();
  });
  mode.value = (await kv.get<"managed" | "manual">("mode")) ?? "";
  if (mode.value === "managed") await startManaged();
  else if (mode.value === "manual") await startManual();
  ready.value = true;
  (window as unknown as { mtvoxApp?: unknown }).mtvoxApp = { cmd, state: { calls, accounts, provState, pairing } };
}

function applyPrefs(): void {
  applyTheme(prefs.value.theme);
  // the managed language counts when someone chose it (admin or user), not the catalog default
  const entry = settings.value["ui.language"];
  const managedLang = entry && entry.src !== "default" ? (entry.v as Lang) : undefined;
  const l: Lang = prefs.value.lang === "system" ? managedLang ?? systemLang() : prefs.value.lang;
  setDocumentLang(l);
}

export async function setPrefs(p: Partial<Prefs>): Promise<void> {
  prefs.value = { ...prefs.value, ...p };
  await kv.set("prefs", prefs.value);
  applyPrefs();
  if (p.dnd !== undefined) await cmd({ cmd: "core.dnd", enable: p.dnd });
}

async function restoreDevices(): Promise<void> {
  const p = prefs.value;
  const sel: Json = { cmd: "audio.select" };
  if (p.source) sel.source = { id: p.source };
  if (p.player) sel.player = { id: p.player };
  if (p.alert) sel.alert = { id: p.alert };
  if (Object.keys(sel).length > 1) await cmd(sel);
}

function deviceInfo(): DeviceInfo {
  const ua = navigator.userAgent;
  const browser = /Firefox\//.test(ua) ? "Firefox" : /Edg\//.test(ua) ? "Edge" : /Chrome\//.test(ua) ? "Chrome" : /Safari\//.test(ua) ? "Safari" : "Browser";
  const os = /Windows/.test(ua) ? "windows" : /Mac OS X/.test(ua) ? "macos" : /Android/.test(ua) ? "android" : /iPhone|iPad/.test(ua) ? "ios" : /Linux/.test(ua) ? "linux" : "unknown";
  return { hostname: t("setup.browser", browser), os: `web/${os}`, os_user: "", app_version: CORE_VERSION, platform: "web" };
}

// --- setup --------------------------------------------------------------
export async function chooseManaged(): Promise<void> {
  mode.value = "managed";
  await kv.set("mode", "managed");
  await startManaged();
}

async function startManaged(): Promise<void> {
  const url = site.value?.prov_url ?? "";
  const r = await cmd({ cmd: "prov.start", url, device: deviceInfo() });
  if (!r.ok) provNotice.value = String(r.message ?? r.error);
}

export async function enrollToken(token: string): Promise<void> {
  await cmd({ cmd: "prov.enroll", token });
}

/** Activation by a one-time token handed over by an embedding host. */
export async function activateWithToken(token: string): Promise<CoreEvent> {
  if (mode.value === "manual" || (mode.value === "managed" && provState.value === "active")) {
    return { ev: "result", ok: true, data: { already: true } };
  }
  if (mode.value !== "managed") await chooseManaged();
  const r = await cmd({ cmd: "prov.enroll", token });
  if (!r.ok) return r;
  // enroll reports a refused token as prov.error, not in its result
  return provState.value === "active" ? r : { ev: "result", ok: false, error: "bad_token" };
}

export async function leaveSetup(): Promise<void> {
  await cmd({ cmd: "prov.stop" });
  mode.value = "";
  pairing.value = null;
  await kv.del("mode");
}

export async function saveManual(a: ManualAccount): Promise<boolean> {
  await kv.set("manual.account", a);
  await kv.set("mode", "manual");
  mode.value = "manual";
  return startManual();
}

async function startManual(): Promise<boolean> {
  const a = await kv.get<ManualAccount>("manual.account");
  if (!a) return false;
  const r = await cmd({
    cmd: "account.add",
    account: {
      id: "manual",
      uri: `sip:${a.user}@${a.domain}`,
      auth_user: a.auth_user || a.user,
      ...(a.ha1 ? { ha1: a.ha1 } : { password: a.password }),
      display_name: a.display_name,
    },
  });
  if (!r.ok) notice(String(r.message || r.error));
  return !!r.ok;
}

export async function changeSetup(): Promise<void> {
  for (const id of Object.keys(accounts.value)) await cmd({ cmd: "account.remove", account: id });
  accounts.value = {};
  await cmd({ cmd: "prov.stop" });
  await kv.del("manual.account");
  await kv.del("mode");
  mode.value = "";
}

export async function reactivate(): Promise<void> {
  await cmd({ cmd: "prov.reset" });
}

export async function syncNow(): Promise<void> {
  await cmd({ cmd: "prov.sync" });
}

export async function setManagedSetting(key: string, value: unknown): Promise<void> {
  const r = await cmd({ cmd: "settings.set", key, value });
  if (!r.ok) notice(r.error === "locked" ? t("notice.locked") : String(r.error));
}

// --- calls ----------------------------------------------------------------
/** @account: that line (a call back on the line the call came in on), else
 * the one chosen in the dialer, else the default one */
export async function dial(to: string, account?: string): Promise<boolean> {
  const target = to.trim();
  if (!target) return false;
  // a name from the contacts dials its number
  const byName = allContacts().find((c) => c.name.toLowerCase() === target.toLowerCase());
  const r = await dialNumber(byName ? byName.number : target.replace(/[\s().-]/g, ""), undefined, account);
  if (r.ok) return true;
  notice(r.error === "no_line" ? t("notice.no_line") : r.error === "not_connected" ? t("notice.not_connected") : t("notice.dial_failed", String(r.error)));
  return false;
}

/** call.dial on @account, the dialer's choice or the default line; when
 * that one isn't connected, another that is (as the phones do); ref: the
 * embedding host's reference */
export async function dialNumber(to: string, ref?: string, account?: string): Promise<CoreEvent> {
  const want = [account, outgoing.value, defaultAccount.value].find((a) => a && accounts.value[a]) ?? "";
  if (!want) return { ev: "result", ok: false, error: "no_line" };
  const up = (id: string) => accounts.value[id]?.state === "registered";
  const acc = up(want) ? want : lines.value.map((l) => l.id).find(up);
  if (!acc) return { ev: "result", ok: false, error: "not_connected" };
  const r = await cmd({ cmd: "call.dial", account: acc, to, ...(ref !== undefined ? { ref } : {}) });
  if (r.ok) outgoing.value = "";
  return r;
}

export const answer = (call: string) => {
  answered.add(call);
  return cmd({ cmd: "call.answer", call });
};
export const decline = (call: string) => {
  answered.add(call);
  return cmd({ cmd: "call.hangup", call, code: 603 });
};
export const hangup = (call: string) => cmd({ cmd: "call.hangup", call });
export const hold = (call: string, on: boolean) => cmd({ cmd: "call.hold", call, hold: on });
export const mute = (call: string, on: boolean) => cmd({ cmd: "call.mute", call, mute: on });
export const dtmf = (call: string, digits: string) => cmd({ cmd: "call.dtmf", call, digits });
// --- voicemail ----------------------------------------------------------
async function vm(c: Json): Promise<any> {
  const r = await cmd(c);
  if (!r.ok) {
    vmError.value = String(r.error ?? "failed");
    if (c.cmd === "voicemail.list" && (r.error === "not_found" || r.error === "not_active")) vmAvailable.value = false;
    return null;
  }
  vmError.value = "";
  return r.data ?? {};
}

export async function vmRefresh(): Promise<void> {
  const d = await vm({ cmd: "voicemail.list" });
  if (!d) return;
  vmMessages.value = d.messages ?? [];
  vmAvailable.value = true;
}

export async function vmAudio(id: string): Promise<string | null> {
  const d = await vm({ cmd: "voicemail.audio", message: id });
  const m = vmMessages.value.find((x) => x.id === id);
  if (d && m && !m.read) await vmMark(id, { read: true });
  return d?.url ?? null;
}

export async function vmMark(id: string, f: { read?: boolean; saved?: boolean }): Promise<void> {
  const d = await vm({ cmd: "voicemail.mark", message: id, ...f });
  if (d) vmMessages.value = vmMessages.value.map((m) => (m.id === id ? { ...m, ...d } : m));
}

export async function vmDelete(id: string): Promise<void> {
  if (await vm({ cmd: "voicemail.delete", message: id })) vmMessages.value = vmMessages.value.filter((m) => m.id !== id);
}

export async function vmLoadGreetings(): Promise<void> {
  const d = await vm({ cmd: "voicemail.greetings" });
  if (d) vmGreetings.value = d;
}

export async function vmGreetingAudio(slot: string): Promise<string | null> {
  return (await vm({ cmd: "voicemail.greeting_audio", slot }))?.url ?? null;
}

export async function vmSaveGreeting(slot: string, wav: ArrayBuffer): Promise<boolean> {
  const d = await vm({ cmd: "voicemail.greeting_save", slot, wav: wav as unknown as Json });
  if (d) vmGreetings.value = d;
  return !!d;
}

export async function vmSetActive(slot: number | null): Promise<void> {
  const d = await vm({ cmd: "voicemail.greeting_active", slot });
  if (d) vmGreetings.value = d;
}

export async function vmDeleteGreeting(slot: string): Promise<void> {
  const d = await vm({ cmd: "voicemail.greeting_delete", slot });
  if (d) vmGreetings.value = d;
}

/** The mailbox's number: setting voicemail.number (*98). */
export function voicemailNumber(): string {
  return String(settings.value["voicemail.number"]?.v ?? "*98");
}

export async function transferToVoicemail(call: string, extension: string): Promise<void> {
  const to = voicemailTarget(String(settings.value["voicemail.transfer_prefix"]?.v ?? "**097#"), extension);
  if (!to) return notice(t("notice.extension_digits"));
  const r = await cmd({ cmd: "call.transfer", call, to });
  if (!r.ok) notice(t("notice.transfer_failed"));
}

export async function transfer(call: string, to: string): Promise<void> {
  const r = await cmd({ cmd: "call.transfer", call, to: to.replace(/[\s().-]/g, "") });
  if (!r.ok) notice(t("notice.transfer_failed"));
}
export const merge = () => cmd({ cmd: "conference.merge" });
export const split = (call: string) => cmd({ cmd: "conference.split", call });
export const join = (call: string) => cmd({ cmd: "conference.add", call });
export const endConference = () => cmd({ cmd: "conference.end" });

// --- contacts and history ---------------------------------------------------
export function allContacts(): Contact[] {
  return [
    ...contacts.value,
    ...directory.value.entries.map((e) => ({ id: `dir:${e.number}`, name: e.name, number: e.number, source: "directory" as const })),
  ];
}

/* contacts.changed or the contacts.list result: one row per contact (its
 * primary number), favourites first */
function setContacts(book: Json): void {
  const names = new Map(((book.books as { id: string; name: string }[]) ?? []).map((b) => [b.id, b.name]));
  const rows = ((book.contacts as BookContact[]) ?? []).map((c) => {
    const phones = c.phones ?? [];
    const primary = phones.find((p) => p.id === c.primary_phone) ?? phones[0];
    const bk = String(c.book ?? "personal");
    return {
      id: String(c.id),
      name: String(c.name ?? ""),
      number: primary?.number ?? "",
      numbers: phones.map((p) => p.number),
      company: c.company ?? "",
      source: bk === "personal" || bk === "device" ? ("local" as const) : ("shared" as const),
      favorite: c.favorite === true,
      bookName: names.get(bk),
      raw: c as unknown as Json,
    };
  });
  contacts.value = rows.sort((a, b) => Number(b.favorite) - Number(a.favorite) || a.name.localeCompare(b.name));
}

/* Contacts this browser kept itself before the address book synced
 * (KV "contacts", 0.1.x): handed to the book once, then kept aside. */
async function migrateLocalContacts(): Promise<void> {
  const old = await kv.get<Contact[]>("contacts");
  if (!old?.length) return;
  const r = await cmd({ cmd: "contacts.import", contacts: old.map((c) => ({ name: c.name, number: c.number })) as never });
  if (!r.ok) return;
  await kv.set("contacts.imported", old);
  await kv.del("contacts");
}

export { numberOf };

/** the user's contact (or directory entry) with this number */
export function contactFor(number: string): Contact | undefined {
  const nn = normalizeNumber(number);
  return allContacts().find((c) => c.number === number || c.numbers?.includes(nn));
}

/** lower case, accents off: « Hélène » finds « helene » */
export const fold = (s: string): string => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

/** what the dialer suggests for @q: a name, a company (accents or not), or
 * digits found in any of a contact's numbers (+1 or not); favourites and
 * the user's own first */
export function contactMatches(q: string, limit = 4): Contact[] {
  const text = fold(q.trim());
  const digits = q.replace(/\D/g, "");
  if (text.length < 2) return [];
  const byDigits = digits.length >= 3 && /^[\d\s().+-]+$/.test(q.trim());
  const nums = (c: Contact) => [c.number, ...(c.numbers ?? [])].map((n) => n.replace(/\D/g, ""));
  const hit = (c: Contact) =>
    byDigits ? nums(c).some((n) => n.includes(digits) || n.replace(/^1(?=\d{10}$)/, "").includes(digits)) : fold(c.name).includes(text) || fold(c.company ?? "").includes(text);
  const rank = (c: Contact) => (c.favorite ? 0 : 2) + (c.source === "directory" ? 1 : 0);
  const out = allContacts().filter(hit);
  out.sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
  // one row per number: a contact and the directory may both have it
  const seen = new Set<string>();
  return out.filter((c) => !seen.has(c.number) && seen.add(c.number)).slice(0, limit);
}

// --- someone: the person card, the after-call panel -------------------------
/** the number whose card is open ("" none) */
export const personNumber = signal("");
/** the call that just ended, for the after-call panel (null: none) */
export const afterCall = signal<HistoryRow | null>(null);
/** a contact to create with this number (Contacts opens its editor) */
export const contactDraft = signal<{ number: string; name: string } | null>(null);

/** the same number written two ways: the last 10 digits (or all, when shorter) */
export function numberKey(n: string): string {
  const d = n.replace(/\D/g, "");
  return d.length > 10 ? d.slice(-10) : d;
}

/** the user's contact or the directory entry with this number */
export function whoIs(number: string): Contact | undefined {
  const k = numberKey(number);
  if (!k) return undefined;
  return allContacts().find((c) => [c.number, ...(c.numbers ?? [])].some((n) => numberKey(n) === k));
}

/** a colleague: their extension is in the directory (chat goes to them) */
export function isColleague(number: string): boolean {
  return directory.value.entries.some((e) => e.number === number);
}

/** the calls with this number, newest first */
export function callsWith(number: string, limit = 30): HistoryRow[] {
  const k = numberKey(number);
  return historyRows()
    .filter((r) => numberKey(r.number) === k)
    .slice(0, limit);
}

/** The after-call panel's row for a call that just ended (as on the phones):
 * none for a missed call, a feature code (*98...) or while another call
 * goes on */
export function afterCallRow(e: HistoryEntry, othersLive: boolean): HistoryRow | null {
  if (e.missed || !e.number || /^[*#]/.test(e.number) || othersLive) return null;
  return { id: e.id, number: e.number, name: e.name ?? e.number, dir: e.dir, missed: false, at: e.at, duration: e.duration, outcome: "", clips: [], pending: false, account: e.account };
}

export function openPerson(number: string): void {
  if (number) personNumber.value = number;
}

/** Contacts, its editor open on a new contact with this number */
export function newContactFor(number: string, name = ""): void {
  contactDraft.value = { number, name: notAName(name) ? "" : name };
  personNumber.value = "";
  afterCall.value = null;
  page.value = "contacts";
}

/** the last number called from here, for redial */
export function lastDialed(): string {
  return historyRows().find((r) => r.dir === "out")?.number ?? "";
}

/** a "name" that is only a number ("672": a directory entry without a name) */
export const notAName = (s: string | undefined): boolean => !s || !/[^0-9+*#() .-]/.test(s);

/** the contact's or directory's name, else the name the call had (the
 * PBX's caller-ID name), else the number; a directory entry that is only
 * the number loses to the call's name */
export function nameFor(uri: string, fallback?: string): string {
  const n = numberOf(uri);
  const known = contactFor(n)?.name;
  if (!notAName(known)) return known!;
  if (!notAName(fallback)) return fallback!;
  return known || fallback || n;
}

/** contacts.save of the whole contact as the page shows it: the core sends
 * only what changed. Without an id (or an unknown one), a new contact. */
export async function saveContact(c: Json): Promise<boolean> {
  const contact = { ...c };
  delete contact.name; // the list's name to show, not a field
  if (!c.id || !contacts.value.some((x) => x.id === c.id)) delete contact.id;
  const r = await cmd({ cmd: "contacts.save", contact });
  if (!r.ok) notice(t("notice.contact_not_saved"));
  return !!r.ok;
}

export async function deleteContact(id: string): Promise<void> {
  await cmd({ cmd: "contacts.delete", contact_id: id });
}

export async function clearHistory(): Promise<void> {
  history.value = [];
  missed.value = 0;
  await kv.set("history", []);
  if (callLog.value.enabled) await cmd({ cmd: "calllog.clear" });
}

// --- the shared call list ---------------------------------------------------
let callLogLimit = 300;

async function refreshCallLog(): Promise<void> {
  const r = await cmd({ cmd: "calllog.list", limit: callLogLimit });
  const d = (r.data ?? {}) as { enabled?: boolean; device?: string; older?: boolean; entries?: CallEntry[] };
  callLog.value = { enabled: !!d.enabled, device: d.device ?? "", older: !!d.older, entries: d.entries ?? [] };
}

export async function moreCallLog(): Promise<void> {
  callLogLimit += 200;
  if (callLog.value.entries.length >= callLogLimit - 200) await cmd({ cmd: "calllog.more" });
  await refreshCallLog();
}

/** A History row: an entry of the shared list, or one of this browser's own calls */
export interface HistoryRow {
  id: string;
  entry?: string; // the shared list's entry id
  number: string;
  name: string;
  dir: "in" | "out";
  missed: boolean;
  at: number;
  duration: number;
  outcome: string;
  clips: string[];
  pending: boolean;
  /** the line to call back on */
  account?: string;
}

function where(d: CallDevice): string {
  switch (d.kind) {
    case "desk":
      return t("history.where_desk");
    case "android":
      return t("history.where_android");
    case "web":
      return t("history.where_web");
    default:
      return d.name || (d.kind === "desktop" ? t("history.where_desktop") : t("history.where_other"));
  }
}

function outcomeText(e: CallEntry, device: string): string {
  const inc = e.dir === "in";
  const on = e.answered_on;
  switch (e.outcome) {
    case "answered":
      if (!inc) return t("history.outgoing");
      if (!on) return t("history.answered");
      if (device && on.device === device) return t("history.here");
      if (on.kind === "desk") return t("history.on_desk");
      if (on.kind === "android") return t("history.on_android");
      if (on.kind === "web") return t("history.on_web");
      return on.name ? t("history.on_named", on.name) : on.kind === "desktop" ? t("history.on_desktop") : t("history.answered");
    case "elsewhere": {
      const ext = e.answered_by?.extension ?? "";
      if (!ext) return t("history.by_other");
      const n = e.answered_by?.name || nameFor(ext, "");
      return t("history.by", n && n !== ext ? `${ext} (${n})` : ext);
    }
    case "voicemail":
      return t("history.voicemail");
    case "forwarded":
      return t("history.forwarded", e.forwarded_to ?? "");
    case "declined":
      return t("history.declined");
    case "missed":
      return t("history.missed");
    case "no_answer":
      return t("history.no_answer");
    case "busy":
      return t("history.busy");
    case "failed":
      return t("history.failed");
    default:
      return "";
  }
}

/* What History shows. With the shared list: its entries, then this
 * browser's own calls that it doesn't have: the ones that just ended (the
 * PBX's entry comes seconds later) and, once no older page is left, the
 * ones from before the list started. Without it: this browser's history. */
export function historyRows(): HistoryRow[] {
  const own = (h: HistoryEntry, pending: boolean): HistoryRow => ({
    id: h.id,
    number: h.number,
    name: nameFor(h.peer, h.name),
    dir: h.dir,
    missed: h.missed,
    at: h.at,
    duration: h.duration,
    outcome: "",
    clips: [],
    pending,
    account: h.account,
  });
  const cl = callLog.value;
  if (!cl.enabled) return history.value.map((h) => own(h, false));
  const rows: HistoryRow[] = cl.entries.map((e) => ({
    id: e.id,
    entry: e.id,
    number: e.number,
    name: nameFor(e.number, e.name || undefined),
    dir: e.dir,
    missed: e.dir === "in" && e.outcome === "missed",
    at: e.at,
    duration: e.talk_s ?? 0,
    outcome: outcomeText(e, cl.device),
    clips: (e.clips ?? []).map((c) => (cl.device && c.device === cl.device ? t("history.clip_here") : t("history.clip_on", where(c)))),
    pending: false,
    account: lineOfExt(e.as),
  }));
  const known = new Set(cl.entries.flatMap((e) => e.sip_call_ids ?? []));
  const newest = cl.entries.length ? cl.entries[0].at : 0;
  const oldest = cl.entries.length ? cl.entries[cl.entries.length - 1].at : Infinity;
  for (const h of history.value) {
    if (known.has(h.id)) continue;
    if (h.at > newest - 120_000) rows.push(own(h, true));
    else if (!cl.older && h.at < oldest - 5_000) rows.push(own(h, false));
  }
  return rows.sort((a, b) => b.at - a.at);
}

export async function deleteHistoryRow(row: HistoryRow): Promise<void> {
  if (row.entry) {
    await cmd({ cmd: "calllog.delete", entry: row.entry });
    return;
  }
  history.value = history.value.filter((h) => h.id !== row.id);
  await kv.set("history", history.value);
}

async function addHistory(c: UiCall): Promise<void> {
  const h = history0.get(c.call);
  history0.delete(c.call);
  const missedCall = c.dir === "in" && !h?.established && !answered.has(c.call);
  answered.delete(c.call);
  const e: HistoryEntry = {
    id: c.call,
    peer: c.peer,
    number: numberOf(c.peer),
    name: nameFor(c.peer, c.peer_name || undefined),
    dir: c.dir as "in" | "out",
    missed: missedCall,
    account: c.account,
    at: Date.now() - (c.duration ?? 0) * 1000,
    duration: c.duration ?? 0,
  };
  if (missedCall) missed.value++;
  history.value = [e, ...history.value].slice(0, 300);
  const panel = afterCallRow(e, calls.value.some((x) => x.call !== c.call && x.state !== "ended"));
  if (panel) afterCall.value = panel;
  await kv.set("history", history.value);
  // the PBX's entry reaches the server about 3 s after the call
  if (callLog.value.enabled) setTimeout(() => void cmd({ cmd: "calllog.sync" }), 6000);
}

// --- events ---------------------------------------------------------------
function onEvent(e: CoreEvent): void {
  onAppEvent(e);
  for (const l of coreListeners) l(e);
}

function onAppEvent(e: CoreEvent): void {
  switch (e.ev) {
    case "account.state": {
      const id = String(e.account);
      const label = id === "manual" ? "1" : id.replace(/^line-/, "");
      const known = id in accounts.value;
      accounts.value = { ...accounts.value, [id]: { state: String(e.state), reason: e.reason as string, label } };
      if (!known) void refreshLines();
      break;
    }
    case "call.state":
      onCall(e as unknown as UiCall);
      break;
    case "call.stats": {
      calls.value = calls.value.map((c) => (c.call === e.call ? { ...c, stats: e } : c));
      break;
    }
    case "call.remote_hold":
      calls.value = calls.value.map((c) => (c.call === e.call ? { ...c, remote_hold: e.hold } : c));
      break;
    case "conference.changed":
      conference.value = (e.calls as string[]) ?? [];
      break;
    case "call.transfer_failed":
      notice(t("notice.transfer_failed"));
      break;
    case "prov.state":
      provState.value = e.state as ProvState;
      if (e.state === "active") pairing.value = null;
      if (e.state === "enrolling") provNotice.value = "";
      break;
    case "prov.pairing":
      pairing.value = { code: String(e.code), expiresAt: Date.now() + Number(e.expires_in) * 1000 };
      break;
    case "prov.revoked":
      notice(e.reason === "device_replaced" ? t("notice.replaced") : t("notice.revoked"));
      accounts.value = {};
      break;
    case "prov.error":
      if (e.op === "enroll") provNotice.value = t("notice.bad_token");
      else if (e.code === "unreachable" && provState.value !== "active") provNotice.value = t("notice.prov_unreachable");
      break;
    case "prov.config":
      provNotice.value = "";
      void refreshLines();
      break;
    case "account.removed": {
      const rest = { ...accounts.value };
      delete rest[String(e.account)];
      accounts.value = rest;
      void refreshLines();
      break;
    }
    case "settings.changed": {
      settings.value = { ...(e.settings as Record<string, SettingEntry>) };
      applyPrefs();
      const ec = settings.value["audio.echo_cancellation"];
      if (ec) void cmd({ cmd: "audio.echo_cancellation", enable: ec.v !== false });
      break;
    }
    case "settings.rejected":
      notice(t("notice.rejected", String(e.key)));
      break;
    case "directory.changed":
      directory.value = { name: e.name as string | undefined, entries: (e.entries as never) ?? [] };
      break;
    case "contacts.changed":
      setContacts(e as unknown as Json);
      break;
    case "chat.changed":
      void refreshChat();
      break;
    case "calllog.changed":
      void refreshCallLog();
      break;
    case "chat.message":
      void onChatMessage(String(e.conversation), e.message as unknown as ChatMsg);
      break;
    case "chat.failed":
      notice(
        e.code === "attachments_off" ? t("chat.files_off") : e.code === "chat_off" ? t("chat.chat_off") : e.code === "not_found" ? t("chat.not_found") : e.code === "too_large" ? t("chat.too_large") : t("chat.not_sent"),
      );
      break;
    case "contacts.rejected":
      notice(t("notice.contact_rejected"));
      break;
    case "diag.mic_level":
      micLevel.value = e as never;
      break;
    default:
      break;
  }
}

function onCall(c: UiCall): void {
  const prev = calls.value.find((x) => x.call === c.call);
  if (c.state === "established") history0.set(c.call, { established: Date.now() });
  else if (!history0.has(c.call)) history0.set(c.call, {});
  if (c.state === "ended") {
    calls.value = calls.value.map((x) => (x.call === c.call ? { ...x, ...c, ended_reason: endReason(c) } : x));
    if (!prev) return;
    void addHistory({ ...prev, ...c });
    if (dtmfCall.value === c.call) dtmfCall.value = "";
    // the card shows why it ended for a moment
    setTimeout(() => (calls.value = calls.value.filter((x) => x.call !== c.call)), 2500);
  } else {
    calls.value = prev ? calls.value.map((x) => (x.call === c.call ? { ...x, ...c } : x)) : [...calls.value, c];
  }
  if (c.state === "incoming" && !prev) onIncoming(c);
  if (!prev && c.state !== "ended") afterCall.value = null; // a new call takes its place
  updateRinging();
}

function endReason(c: UiCall): string {
  const code = Number((c as Json).code ?? 0);
  switch (code) {
    case 0:
    case 200:
      return t("end.ended");
    case 404:
    case 484:
      return t("end.not_found");
    case 408:
    case 480:
      return t("end.no_answer");
    case 486:
    case 600:
      return t("end.busy");
    case 487:
      return t("end.cancelled");
    case 403:
      return t("end.forbidden");
    case 488:
    case 606:
      return t("end.incompatible");
    case 503:
      return t("end.service");
    case 603:
      return t("end.declined");
    default:
      return code >= 400 ? t("end.failed", code) : t("end.ended");
  }
}

// --- ringing, notifications --------------------------------------------------
let ringing = false;
function updateRinging(): void {
  const incoming = calls.value.some((c) => c.state === "incoming");
  const busy = calls.value.some((c) => c.state === "established" || c.state === "outgoing" || c.state === "ringing" || c.state === "early");
  const want = incoming && !busy;
  if (want !== ringing) {
    ringing = want;
    void cmd(want ? { cmd: "audio.ring" } : { cmd: "audio.ring", stop: true });
  }
  if (!incoming) void closeNotifications();
}

async function onIncoming(c: UiCall): Promise<void> {
  const who = nameFor(c.peer, c.peer_name || undefined);
  if (document.visibilityState === "visible" && document.hasFocus()) return;
  if (!("Notification" in window) || Notification.permission !== "granted") return;
  const reg = await navigator.serviceWorker?.getRegistration();
  const opts = {
    body: t("notify.incoming_body", who),
    tag: `call-${c.call}`,
    icon: "/icon-192.png",
    badge: "/icon-192.png",
    requireInteraction: true,
    data: { call: c.call },
    actions: [
      { action: "answer", title: t("call.answer") },
      { action: "decline", title: t("call.decline") },
    ],
  };
  if (reg) await reg.showNotification(t("call.incoming"), opts as NotificationOptions);
  else new Notification(t("call.incoming"), opts);
}

async function closeNotifications(): Promise<void> {
  const reg = await navigator.serviceWorker?.getRegistration();
  for (const n of (await reg?.getNotifications()) ?? []) if (n.tag.startsWith("call-")) n.close();
}

/** the service worker forwards notification clicks */
export function onServiceWorkerMessage(m: { type: string; action?: string; call?: string; chat?: string }): void {
  if (m.type === "notification" && m.chat) {
    page.value = "messages";
    void openChat(m.chat);
    return;
  }
  if (m.type !== "notification" || !m.call) return;
  if (m.action === "answer" || m.action === "") void answer(m.call);
  else if (m.action === "decline") void decline(m.call);
}

export async function askNotifications(): Promise<void> {
  if (!("Notification" in window)) return;
  const p = await Notification.requestPermission();
  await setPrefs({ notifications: p === "granted" });
}

// --- chat between users ------------------------------------------------------
/** "ext:<enterprise>/<extension>": the directory's or a contact's name, else the extension */
export function chatName(address: string | undefined): string {
  const ext = (address ?? "").split("/").pop() ?? "";
  const company = (address ?? "").slice(4).split("/")[0];
  if (address && company !== chatMe.value.slice(4).split("/")[0]) {
    // another company's: its name with the company's
    const p = chatPartners.value.find((x) => x.company === company);
    const e = p?.entries.find((x) => x.address === address);
    return p ? `${e?.name ?? ext} · ${p.name}` : ext;
  }
  return contactFor(ext)?.name ?? ext;
}

/** the text a conversation list shows for a message */
export function chatPreview(m: ChatMsg): string {
  if (m.deleted) return t("chat.deleted");
  if (m.body) return m.body.replace(/\s+/g, " ");
  return m.attachments?.length ? t("chat.file", m.attachments[0].name) : "";
}

export function convName(c: ChatConv): string {
  return c.title || chatName(c.peer);
}

async function refreshChat(): Promise<void> {
  const r = await cmd({ cmd: "chat.list" });
  if (!r.ok) return;
  const d = r.data as unknown as {
    enabled: boolean;
    files: boolean;
    unread: number;
    me: string;
    mine?: string[];
    partners?: ChatPartner[];
    conversations: ChatConv[];
  };
  chatEnabled.value = d.enabled;
  chatFiles.value = !!d.files;
  chatUnread.value = d.unread;
  chatMe.value = d.me;
  chatMine.value = d.mine?.length ? d.mine : d.me ? [d.me] : [];
  if (JSON.stringify(d.partners ?? []) !== JSON.stringify(chatPartners.value)) chatPartners.value = d.partners ?? [];
  if (chatFilter.value && !chatMine.value.includes(chatFilter.value)) chatFilter.value = "";
  const list = [...d.conversations].sort((a, b) => b.last_at - a.last_at);
  // the first message to someone: the server named the conversation
  const open = chatOpen.value;
  if (open.startsWith("new:") && !list.some((c) => c.id === open)) {
    const [to, as] = open.slice(4).split("|");
    const named = list.find((c) => c.kind === "direct" && c.peer === to && c.as === (as || d.me));
    if (named) chatOpen.value = named.id;
  }
  chats.value = list;
  await loadChatMessages();
  void markChatRead();
}

async function loadChatMessages(): Promise<void> {
  const id = chatOpen.value;
  if (!id) {
    chatMessages.value = [];
    return;
  }
  const r = await cmd({ cmd: "chat.messages", conversation: id, limit: 200 });
  if (id !== chatOpen.value) return;
  chatMessages.value = r.ok ? ((r.data as unknown as { messages: ChatMsg[] }).messages ?? []) : [];
}

function chatOnScreen(): boolean {
  return page.value === "messages" && document.visibilityState === "visible" && document.hasFocus();
}

async function markChatRead(): Promise<void> {
  const id = chatOpen.value;
  if (!id || id.startsWith("new:") || !chatOnScreen()) return;
  if ((chats.value.find((c) => c.id === id)?.unread ?? 0) > 0) await cmd({ cmd: "chat.read", conversation: id });
}

export async function openChat(id: string): Promise<void> {
  chatOpen.value = id;
  chatMessages.value = [];
  await loadChatMessages();
  void markChatRead();
}

/** the direct conversation with an extension of the company (a new one when none) */
export async function openChatWith(extension: string): Promise<void> {
  const ext = extension.trim();
  if (!/^[0-9*#]{1,16}$/.test(ext)) return;
  const enterprise = chatMe.value.slice(4).split("/")[0];
  await openChatWithAddress(`ext:${enterprise}/${ext}`);
}

/** the direct conversation with an address, ours or another company's (a new one when none) */
export async function openChatWithAddress(address: string): Promise<void> {
  // written as the extension shown (all: line 1's)
  const as = chatFilter.value || chatMe.value;
  const found = chats.value.find((c) => c.kind === "direct" && c.peer === address && c.as === as);
  page.value = "messages";
  // the core's id until the server names it (prov/chat.ts pendingId)
  await openChat(found ? found.id : `new:${address}${as === chatMe.value ? "" : "|" + as}`);
}

export const CHAT_MAX_FILE = 10 * 1024 * 1024;

export async function sendChat(body: string, files: File[] = []): Promise<boolean> {
  const text = body.trim();
  const id = chatOpen.value;
  if ((!text && !files.length) || !id) return false;
  if (files.some((f) => f.size > CHAT_MAX_FILE)) {
    notice(t("chat.too_large"));
    return false;
  }
  const extra = files.length ? { files: files.map((f) => ({ blob: f, name: f.name, type: f.type || undefined })) } : {};
  const [to, from] = id.slice(4).split("|");
  const r = await cmd(
    (id.startsWith("new:")
      ? { cmd: "chat.send", to, body: text, ...extra, ...(from ? { from } : {}) }
      : { cmd: "chat.send", conversation: id, body: text, ...extra }) as unknown as Json,
  );
  if (!r.ok) notice(t("chat.not_sent"));
  return !!r.ok;
}

export async function editChat(message: string, body: string): Promise<void> {
  if (body.trim()) await cmd({ cmd: "chat.edit", message, body: body.trim() });
}

export async function deleteChat(message: string): Promise<void> {
  await cmd({ cmd: "chat.delete", message });
}

/** my messages the others have read up to */
export function chatSeenUpTo(): number {
  const c = chats.value.find((x) => x.id === chatOpen.value);
  return Math.max(0, ...(c?.members ?? []).filter((m) => m.address !== (c?.as || chatMe.value)).map((m) => m.read_seq));
}

/** "620" of "ext:mt/620" */
export function extOf(address: string | undefined): string {
  return (address ?? "").split("/").pop() ?? "";
}

/** the extension the open conversation is written as */
export function chatOpenAs(): string {
  const open = chatOpen.value;
  const c = chats.value.find((x) => x.id === open);
  if (c) return c.as;
  return open.startsWith("new:") ? open.slice(4).split("|")[1] || chatMe.value : "";
}

async function onChatMessage(conversation: string, m: ChatMsg): Promise<void> {
  if (chatOnScreen() && conversation === chatOpen.value) return;
  const who = chatName(m.from);
  if (document.visibilityState === "visible" && document.hasFocus()) {
    notice(t("chat.toast", who, m.body.slice(0, 140)));
    return;
  }
  if (embedded || !("Notification" in window) || Notification.permission !== "granted") return;
  const reg = await navigator.serviceWorker?.getRegistration();
  const opts = { body: m.body.slice(0, 160), tag: `chat-${conversation}`, icon: "/icon-192.png", badge: "/icon-192.png", data: { chat: conversation } };
  if (reg) await reg.showNotification(who, opts);
  else {
    const n = new Notification(who, opts);
    n.onclick = () => {
      focus();
      page.value = "messages";
      void openChat(conversation);
    };
  }
}
